import { z } from "zod";

export const environmentSchema = z.enum(["local-dev", "production"]);
export type EnvironmentName = z.infer<typeof environmentSchema>;

export const workspaceKindSchema = z.enum(["personal", "domain"]);
export const entitlementSchema = z.enum([
  "free",
  "trial",
  "paid",
  "payment_required",
]);
export const roleSchema = z.enum(["owner", "billing_admin", "member"]);
export const accountStatusSchema = z.enum([
  "active",
  "paused",
  "reconnect_required",
  "scope_required",
  "deleting",
]);

export const detectorStatusSchema = z.enum([
  "draft",
  "shadow",
  "canary",
  "active",
  "retired",
]);
export const policyOutcomeSchema = z.enum([
  "allow",
  "review",
  "auto_spam",
  "skipped",
]);
export const evaluationLabelSchema = z.enum([
  "spam",
  "wanted",
  "ambiguous",
  "provider_error",
]);

export const decisionStateSchema = z.object({
  from: z.string().max(512),
  reply_to: z.string().max(512).nullable(),
  subject: z.string().max(998),
  body_current_message: z.string().max(12_000),
  replies_to_prior_message: z.boolean(),
  known_correspondent: z.boolean(),
  recipient_requested_contact: z.boolean(),
  list_unsubscribe_present: z.boolean(),
  provider_labels: z.array(z.string().max(64)).max(64),
  locale_hint: z.string().max(32).nullable(),
  body_truncated: z.boolean(),
});
export type DecisionState = z.infer<typeof decisionStateSchema>;

export const detectorConfigSchema = z
  .object({
    id: z.string(),
    userId: z.string(),
    status: detectorStatusSchema,
    model: z.literal("jev-1.13.0"),
    prompt: z.string().min(200).max(20_000),
    promptHash: z.string(),
    extractionVersion: z.string(),
    policyVersion: z.string(),
    reviewThreshold: z.number().min(0).max(1),
    autoSpamThreshold: z.number().min(0).max(1),
    bodyCharacterLimit: z.number().int().min(500).max(12_000),
    allowlistPolicy: z.string(),
    canaryPercentage: z.number().min(0).max(1),
    shadowStartedAt: z.string().nullable(),
    canaryStartedAt: z.string().nullable(),
    evaluationRunId: z.string().nullable(),
    activatedAt: z.string().nullable(),
    retiredAt: z.string().nullable(),
    createdAt: z.string(),
  })
  .refine((value) => value.reviewThreshold <= value.autoSpamThreshold, {
    message: "Review threshold must not exceed the auto-spam threshold",
  });
export type DetectorConfig = z.infer<typeof detectorConfigSchema>;

export const mailboxChangedJobSchema = z.object({
  type: z.literal("mailbox.changed"),
  accountId: z.string(),
  targetCursor: z.string(),
  sourceEventId: z.string(),
  observedAt: z.iso.datetime(),
  idempotencyKey: z.string(),
});

export const messageClassifyJobSchema = z.object({
  type: z.literal("message.classify"),
  accountId: z.string(),
  providerMessageId: z.string(),
  providerThreadId: z.string().optional(),
  observedCursor: z.string(),
  observedAt: z.iso.datetime(),
  sourceEventId: z.string(),
  configId: z.string(),
  recoveryWindow: z
    .object({ from: z.iso.datetime(), until: z.iso.datetime() })
    .optional(),
  idempotencyKey: z.string(),
});

export const billingRecoveryReviewJobSchema = z.object({
  type: z.literal("mailbox.billing-recovery-review"),
  recoveryId: z.string(),
  attemptId: z.string(),
  userId: z.string(),
  accountId: z.string(),
  from: z.iso.datetime(),
  until: z.iso.datetime(),
  pageToken: z.string().optional(),
  idempotencyKey: z.string(),
});

export const evaluationJobSchema = z.object({
  type: z.enum(["evaluation.run", "detector.repair"]),
  userId: z.string(),
  configId: z.string(),
  runId: z.string(),
  correctionId: z.string().optional(),
  idempotencyKey: z.string(),
});

export const evaluationContributionJobSchema = z.object({
  type: z.literal("evaluation.contribution.capture"),
  userId: z.string(),
  accountId: z.string(),
  contributionId: z.string(),
  idempotencyKey: z.string(),
});

export const evaluationQueueJobSchema = z.union([
  evaluationJobSchema,
  evaluationContributionJobSchema,
]);

export const billingJobSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("billing.event"),
    stripeEventId: z.string(),
    idempotencyKey: z.string(),
  }),
  z.object({
    type: z.literal("billing.quantity.sync"),
    workspaceId: z.string(),
    idempotencyKey: z.string(),
  }),
]);

export const queueJobSchema = z.union([
  mailboxChangedJobSchema,
  messageClassifyJobSchema,
  billingRecoveryReviewJobSchema,
  evaluationJobSchema,
  evaluationContributionJobSchema,
  billingJobSchema,
]);
export type QueueJob = z.infer<typeof queueJobSchema>;
export type MailboxChangedJob = z.infer<typeof mailboxChangedJobSchema>;
export type MessageClassifyJob = z.infer<typeof messageClassifyJobSchema>;
export type BillingRecoveryReviewJob = z.infer<
  typeof billingRecoveryReviewJobSchema
>;
export type EvaluationJob = z.infer<typeof evaluationJobSchema>;
export type EvaluationContributionJob = z.infer<
  typeof evaluationContributionJobSchema
>;
export type EvaluationQueueJob = z.infer<typeof evaluationQueueJobSchema>;
export type BillingJob = z.infer<typeof billingJobSchema>;

export const createDetectorConfigSchema = z
  .object({
    prompt: z.string().min(200).max(20_000),
    reviewThreshold: z.number().min(0).max(1),
    autoSpamThreshold: z.number().min(0).max(1),
    bodyCharacterLimit: z.number().int().min(500).max(12_000).default(6_000),
  })
  .refine((value) => value.reviewThreshold <= value.autoSpamThreshold, {
    message: "Review threshold must not exceed the auto-spam threshold",
  });

export const pubSubEnvelopeSchema = z.object({
  message: z
    .object({
      data: z.string(),
      messageId: z.string().optional(),
      message_id: z.string().optional(),
      publishTime: z.string().optional(),
      publish_time: z.string().optional(),
    })
    .refine((message) => message.messageId || message.message_id, {
      message: "A Pub/Sub message ID is required",
    })
    .transform((message) => ({
      data: message.data,
      messageId: message.messageId ?? message.message_id!,
      publishTime: message.publishTime ?? message.publish_time,
    })),
  subscription: z.string(),
});

export const gmailPushDataSchema = z.object({
  emailAddress: z.email(),
  historyId: z
    .union([z.string(), z.number().int().nonnegative().safe()])
    .transform(String),
});

export interface ApiErrorBody {
  error: { code: string; message: string; requestId: string };
}

export interface SessionUser {
  id: string;
  email: string;
  name: string;
}

export interface AdminMetrics {
  users: number;
  mailboxesConnected: number;
  emailsProcessed: number;
  emailsMarkedSpam: number;
  emailsMarkedNotSpam: number;
}

export interface AdminErrorEvent {
  id: string;
  timestamp: string;
  service: string;
  message: string;
  level: string;
  outcome: string | null;
  statusCode: number | null;
  rayId: string | null;
  requestId: string | null;
  cloudflareUrl: string;
}

export interface WorkspaceSummary {
  id: string;
  kind: z.infer<typeof workspaceKindSchema>;
  hostedDomain: string | null;
  role: z.infer<typeof roleSchema>;
  entitlement: z.infer<typeof entitlementSchema>;
  trialEndsAt: string | null;
  activeMemberQuantity: number;
  billingPlan: "free" | "legacy_monthly" | "annual";
  billingRecovery: {
    id: string;
    paymentRequiredAt: string;
    restoredAt: string;
    status:
      "pending" | "queueing" | "queued" | "declined" | "completed" | "failed";
    messageCount: number;
  } | null;
}

export interface EmailAccountSummary {
  id: string;
  workspaceId: string;
  provider: "gmail";
  displayAddress: string;
  status: z.infer<typeof accountStatusSchema>;
  watchExpiresAt: string | null;
  healthStatus: "healthy" | "degraded";
  lastErrorCode: string | null;
}

export interface PipelineCoverageSummary {
  classifiedNegative: number;
  classifiedPositive: number;
  skippedIneligible: number;
  skippedMissing: number;
  neverReachedOrStalled: number;
  measuredSince: string;
}

export interface AutoSpamItem {
  decisionId: string;
  emailAccountId: string;
  actionTime: string;
  probability: number;
  configId: string;
  model: string;
  providerActionStatus: "confirmed" | "unconfirmed" | "failed";
  message: { sender: string; subject: string; deepLink: string | null } | null;
  unavailable: boolean;
  unavailableReason:
    | "gmail_not_found"
    | "account_reconnect_required"
    | "account_scope_required"
    | "provider_error"
    | null;
  correctedAt: string | null;
  correctionId: string | null;
  evidenceRetained: boolean;
  repairStatus: "queued" | "running" | "passed" | "needs_review" | null;
  replacementConfigId: string | null;
  replacementConfigStatus:
    "draft" | "shadow" | "canary" | "active" | "retired" | null;
  repairRunStatus:
    "queued" | "running" | "passed" | "failed" | "needs_review" | null;
}

export const digestFrequencies = [
  "off",
  "weekly",
  "biweekly",
  "monthly",
] as const;
export type DigestFrequency = (typeof digestFrequencies)[number];
export const digestFrequencyLabels: Record<DigestFrequency, string> = {
  weekly: "Weekly",
  biweekly: "Every two weeks",
  monthly: "Monthly",
  off: "Off",
};
export interface SpamDigestPreferences {
  frequency: DigestFrequency;
  nextDeliveryAt: string | null;
}

// Shared presentation for the recent-spam page and its email counterpart.
export function recentSpamPresentation(item: AutoSpamItem) {
  const unavailableCopy =
    item.unavailableReason === "account_reconnect_required" ||
    item.unavailableReason === "account_scope_required"
      ? {
          subject: "Auto-spam action",
          sender: "Reconnect Gmail to load message details",
        }
      : item.unavailableReason === "gmail_not_found"
        ? {
            subject: "Message no longer exists in Gmail",
            sender: "Content-free audit only",
          }
        : {
            subject: "Message details temporarily unavailable",
            sender: "Content-free audit only",
          };
  return {
    subject: item.message?.subject || unavailableCopy.subject,
    sender: item.message?.sender || unavailableCopy.sender,
    score: Math.round(item.probability * 100),
    restored: Boolean(item.correctedAt),
    openUrl: item.message?.deepLink ?? null,
  };
}
